import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Clock, MessageCircle, LogOut, RefreshCw, AlertTriangle } from "lucide-react";

export default function PendingActivationPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // ✨ حالة الموظف: المتجر (المالك) غير مفعّل
  const isEmployee = !!user?.is_employee;

  // التحقق مما إذا كانت التجربة قد انتهت
  const isTrialExpired = user?.trial_expired || (user?.license_type === "none" && user?.trial_end_date);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleRefresh = async () => {
    await refreshUser();
    // إذا تم التفعيل، انتقل للوحة التحكم
    if (user?.is_active) {
      navigate(isEmployee && user?.role === "cashier" ? "/pos" : "/dashboard");
    }
  };

  // الانتقال لصفحة الدعم الفني
  const goToSupport = () => {
    window.location.href = '/support';
  };

  // ✨ صفحة مخصصة للموظف عند تعطل المتجر
  if (isEmployee) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" dir="rtl">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/20 via-transparent to-transparent" />
        <Card className="w-full max-w-lg bg-slate-900/80 backdrop-blur-2xl border-white/10 shadow-2xl relative z-10">
          <CardHeader className="text-center pb-4">
            <div className="flex flex-col items-center gap-4 mb-2">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-2xl shadow-amber-500/30">
                <AlertTriangle className="w-12 h-12 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-white tracking-tight">
                المتجر غير مفعّل حالياً
              </CardTitle>
              <p className="text-slate-400 text-sm">
                مرحباً <span className="text-amber-400 font-semibold">{user?.name}</span> 👋
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-4 text-amber-200 text-sm leading-relaxed">
              🔔 حسابك كموظف <span className="font-semibold">صحيح وفعّال</span>، لكن اشتراك متجرك (المالك) منتهي أو غير مفعّل حالياً.
              <br /><br />
              <span className="font-semibold text-white">المطلوب:</span> تواصل مع <span className="text-amber-300">صاحب المتجر</span> ليقوم بتفعيل/تجديد اشتراكه. بمجرد ما يصير المتجر فعّال، تقدر تدخل مباشرة بدون أي تعديل على حسابك.
            </div>

            <div className="bg-slate-800/50 rounded-xl p-3 space-y-1 text-sm">
              <p className="text-slate-300"><span className="text-slate-500">الاسم:</span> {user?.name}</p>
              <p className="text-slate-300"><span className="text-slate-500">الإيميل:</span> {user?.email}</p>
              <p className="text-slate-300"><span className="text-slate-500">الدور:</span> {user?.role || "موظف"}</p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleRefresh} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="employee-refresh-btn">
                <RefreshCw className="w-4 h-4 me-2" />
                تحقق من التفعيل
              </Button>
              <Button onClick={handleLogout} variant="outline" className="flex-1 border-slate-600 text-slate-300" data-testid="employee-logout-btn">
                <LogOut className="w-4 h-4 me-2" />
                تسجيل الخروج
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      dir="rtl"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className={`absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] ${isTrialExpired ? 'from-red-900/20' : 'from-amber-900/20'} via-transparent to-transparent`} />
      
      <Card className="w-full max-w-lg bg-slate-900/80 backdrop-blur-2xl border-white/10 shadow-2xl relative z-10">
        <CardHeader className="text-center pb-4">
          <div className="flex flex-col items-center gap-4 mb-4">
            {/* Icon */}
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl ${
              isTrialExpired 
                ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30' 
                : 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/30'
            }`}>
              {isTrialExpired ? (
                <AlertTriangle className="w-12 h-12 text-white" />
              ) : (
                <Clock className="w-12 h-12 text-white" />
              )}
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white tracking-tight">
                {isTrialExpired ? 'انتهت التجربة المجانية' : 'بانتظار التفعيل'}
              </CardTitle>
              <p className="text-slate-400 text-sm mt-2">
                {isTrialExpired 
                  ? 'انتهت فترة التجربة المجانية (7 أيام) - اشترك الآن للاستمرار' 
                  : 'تم إنشاء حسابك بنجاح - يحتاج تفعيل من المدير'
                }
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* User Info */}
          <div className="bg-slate-800/50 rounded-xl p-4 space-y-2">
            <p className="text-slate-300">
              <span className="text-slate-500">الاسم:</span> {user?.name}
            </p>
            <p className="text-slate-300">
              <span className="text-slate-500">الإيميل:</span> {user?.email}
            </p>
            <p className="text-slate-300">
              <span className="text-slate-500">المتجر:</span> {user?.shop_name}
            </p>
          </div>

          {/* Pricing Info */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-4">
            <h3 className="text-emerald-400 font-bold mb-3">السعر الموحد:</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-300">الترخيص الدائم</span>
                <span className="text-emerald-400 font-bold">690,000 د.ع</span>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-400" />
              طريقة الدفع:
            </h3>
            <div className="flex justify-center">
              <div className="text-center bg-emerald-900/30 px-6 py-3 rounded-lg border border-emerald-500/30">
                <MessageCircle className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                <p className="text-emerald-400 text-lg">تواصل مع الدعم الفني</p>
                <p className="text-white text-sm">للحصول على تفاصيل الدفع</p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-900/50 rounded-xl p-4 text-center">
            <p className="text-slate-400 text-sm">
              استخدم صفحة الدعم الفني لإرسال طلب تفعيل
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-700">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 me-2" />
              تحديث الحالة
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 me-2" />
              تسجيل الخروج
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
